/**
 * Service to handle Office Add-in cache management
 */
class CacheService {
  constructor() {
    this.cacheVersion = '1.0.0'
    this.storageKey = 'tops2-cache-version'
  }

  /**
   * Check if cache needs to be cleared based on version
   */
  async checkCacheVersion() {
    try {
      if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
        const settings = Office.context.roamingSettings
        const storedVersion = settings.get(this.storageKey)
        
        // Get current version from version service
        const VersionService = (await import('./VersionService.js')).default
        const currentVersion = await VersionService.getDisplayVersion()
        
        if (storedVersion !== currentVersion) {
          console.log(`Cache version mismatch: stored=${storedVersion}, current=${currentVersion}`)
          await this.clearAddInCache()
          
          // Update stored version
          settings.set(this.storageKey, currentVersion)
          await new Promise((resolve) => {
            settings.saveAsync(resolve)
          })
          
          return true // Cache was cleared
        }
      }
    } catch (error) {
      console.warn('Could not check cache version:', error)
    }
    
    return false // Cache was not cleared
  }

  /**
   * Clear add-in cache programmatically
   */
  async clearAddInCache() {
    try {
      // Clear roaming settings
      if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
        const settings = Office.context.roamingSettings
        
        // Get all keys and clear non-essential ones
        const keys = settings.getKeys ? settings.getKeys() : []
        keys.forEach(key => {
          if (key !== this.storageKey && !key.startsWith('auth-')) {
            settings.remove(key)
          }
        })
        
        await new Promise((resolve) => {
          settings.saveAsync(resolve)
        })
      }

      // Clear browser cache if available
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames.map(cacheName => {
            if (cacheName.includes('tops2') || cacheName.includes('outlook-addin')) {
              return caches.delete(cacheName)
            }
          })
        )
      }

      // Force reload of static assets by adding cache-busting
      this.bustStaticCache()

      console.log('✅ Add-in cache cleared successfully')
      return true
    } catch (error) {
      console.error('❌ Failed to clear add-in cache:', error)
      return false
    }
  }

  /**
   * Add cache-busting parameters to static assets
   */
  bustStaticCache() {
    try {
      const timestamp = Date.now()
      
      // Update CSS links
      document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
        if (link.href && !link.href.includes('?v=')) {
          link.href = `${link.href}?v=${timestamp}`
        }
      })
      
      // Update script sources (be careful with this)
      document.querySelectorAll('script[src]').forEach(script => {
        if (script.src && !script.src.includes('?v=') && !script.src.includes('office.js')) {
          const newScript = document.createElement('script')
          newScript.src = `${script.src}?v=${timestamp}`
          newScript.async = script.async
          newScript.defer = script.defer
          script.parentNode?.replaceChild(newScript, script)
        }
      })
    } catch (error) {
      console.warn('Could not bust static cache:', error)
    }
  }

  /**
   * Show user instructions for manual cache clearing
   */
  getUserCacheClearInstructions() {
    const isWindows = navigator.platform.toLowerCase().includes('win')
    const instructions = []

    if (isWindows) {
      instructions.push('Windows Instructions:')
      instructions.push('1. Close Outlook completely')
      instructions.push('2. Press Windows + R, type: %localappdata%\\Microsoft\\Office\\16.0\\Wef')
      instructions.push('3. Delete all files and folders in the Wef directory')
      instructions.push('4. Restart Outlook')
    } else {
      instructions.push('Mac Instructions:')
      instructions.push('1. Close Outlook completely')
      instructions.push('2. Clear browser cache in Outlook preferences')
      instructions.push('3. Restart Outlook')
    }

    instructions.push('')
    instructions.push('Alternative: Try refreshing the add-in task pane (F5 or Ctrl+R)')

    return instructions.join('\n')
  }

  /**
   * Force manifest refresh by reloading the add-in
   */
  async forceManifestRefresh() {
    try {
      if (typeof Office !== 'undefined' && Office.context) {
        // Show user notification about refresh
        this.showRefreshNotification()
        
        // Try to reload the add-in
        setTimeout(() => {
          window.location.reload()
        }, 2000)
        
        return true
      }
    } catch (error) {
      console.error('Could not force manifest refresh:', error)
      return false
    }
  }

  /**
   * Show notification about refresh/cache clearing
   */
  showRefreshNotification() {
    // Create a notification element
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #004225;
      color: white;
      padding: 12px 24px;
      border-radius: 6px;
      z-index: 10000;
      font-family: Segoe UI, sans-serif;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `
    notification.textContent = 'Refreshing add-in to load latest version...'
    
    document.body.appendChild(notification)
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 3000)
  }
}

export default new CacheService()
